import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, Subject, Subscription } from "rxjs";
import { filter, map, switchMap, takeUntil } from "rxjs/operators";
import { SubSink } from "subsink";
import { AbstractAuthorizationService } from "./authorization/services/abstractAuthorization.service";
import { i18nActions } from "./i18n/state/actions";
import { IUser } from "./interfaces";
import { Constants } from "./interfaces/constants";
import { NavigationTarget, SettingTarget } from "./interfaces/helpTarget";
import { IStageMark } from "./interfaces/stageMark";
import { LogClass, Logger } from "./logger";
import { NotificationLevel } from "./notifications/notification";
import { NotificationActions } from "./notifications/state/actions";
import { NavigationService } from "./services";
import { AbstractSettingsService } from "./settings/abstractSettingsService";
import { AbstractSubstituteService } from "./substitute/substitute.service";
import { UserFacade } from "./user/services/user.facade";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { EventMessage, EventType, InteractionStatus } from "@azure/msal-browser";
import { I18nService } from "./i18n/services";
import { environment } from "../environments/environment";

@LogClass({ source: "Root" })
@Component({
  selector: "circ-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false
})
export class AppComponent implements OnDestroy, OnInit {
  private subSink = new SubSink();
  private readonly _destroying$ = new Subject<void>();
  private logger: Logger;
  public title = "circulations-app";

  user$: Observable<IUser>;
  $stageMark: Observable<IStageMark>;
  subscription: Subscription;
  /**
   *
   */
  constructor(
    private userFacade: UserFacade,
    private store: Store,
    private settingsService: AbstractSettingsService,
    private navigate: NavigationService,
    private authorizationService: AbstractAuthorizationService,
    private substituteService: AbstractSubstituteService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private i18nService: I18nService
  ) { }

  ngOnInit() {
    this.msalService.instance.initialize();
    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();

        if (!this.user$) {
          this.user$ = this.userFacade.getUser();
          this.$stageMark = this.settingsService.GetStageMark();
          this.userFacade.loadUser();
          this.subSink.sink = this.user$
            .pipe(map((user) => user.roles))
            .subscribe((roles) => {
              this.authorizationService.update(roles);
              if (this.authorizationService.isUnknown(roles)) {
                this.navigate.toError(
                  "Unknown User! Please ask your Administrator for help."
                );
              }
            });
          this.i18nService.init(environment.i18n.options)

          this.subSink.sink = this.user$
            .pipe(
              map((user) => user.id),
              filter((id) => !!id),
              switchMap((id) => this.substituteService.getIsActive(id)),
              filter((active) => active)
            )
            .subscribe(() => {
              const notification = {
                content: "notification.substitute.active",
                level: "warning" as NotificationLevel,
                hideAfter: Constants.numbers.notificationShowTime.forLongMessages
              };
              this.store.dispatch(
                NotificationActions.showNotification({ notification })
              );
            });
        }
      })
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result !== null && result.account !== null) {
          this.msalService.instance.setActiveAccount(result.account);

          if(sessionStorage.getItem('redirectUri') !== null) {
            const url = sessionStorage.getItem('redirectUri');
            if(this.isValidHttpUrl(url)){
              this.navigate.toFullUrl(url.substring(url.indexOf('/?')));
            }
            else{
              this.navigate.to(sessionStorage.getItem('redirectUri').split('/'));
            }
          }
          else{
            this.navigate.toCirculationOverview();
          }
        }
      },
      error: (error) => console.error(error)
    });


  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  toggleLanguageClick(): void {
    this.store.dispatch(i18nActions.toggleLanguage());
  }

  aboutMeClick(): void {
    this.subSink.sink = this.settingsService
      .GetSettingLink(
        SettingTarget[SettingTarget.NavigationTarget],
        NavigationTarget[NavigationTarget.AboutMe]
      )
      .subscribe((v) => window.open(v.default, "_blank"));
  }

  logoutClick(): void {
    this.msalService.logoutRedirect();
    this.msalService.instance.setActiveAccount(null);
  }

  toInformationClick(): void {
    this.subSink.sink = this.settingsService
      .GetSettingLink(
        SettingTarget[SettingTarget.NavigationTarget],
        NavigationTarget[NavigationTarget.Ece]
      )
      .subscribe((v) => window.open(v.default, "_blank"));
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public onNavClick(target: string): void {
    this.logger.debug(`Navigation to ${target}`);
    this.navigate[target]();
  }

  public isValidHttpUrl(string) {
  let url;
  
  try {
    url = new URL(string);
  } catch (_) {
    return false;  
  }

  return url.protocol === "http:" || url.protocol === "https:";
}
}
